import React, { useEffect, useState, useRef } from 'react';
import { Form } from 'react-bootstrap';
import ModalCustom from '../../UI/modal';
import Button from '../../UI/button';
import tripsService from '../../../services//trips/trips.service';
import { useMoveFolder } from './use-move-folder';
import { useTrip } from '../../contexts/trip-context';

type Props = {
  show: boolean;
  handleClose: () => void;
  tripId?: string;
  destinationId?: string;
  folderId?: string;
  folderName?: string;
  deleteAfterMove?: boolean;
};

interface TripOption {
  id: string;
  title: string;
  destinationCount: number;
}

const MoveFolderModal = ({
  show,
  handleClose,
  tripId: propTripId,
  destinationId: propDestinationId,
  folderId: propFolderId,
  folderName: propFolderName,
  deleteAfterMove = false
}: Props) => {
  const { trip, activeFolderIds } = useTrip();

  // Use context mode if props are not provided
  const tripId = propTripId || trip?.id || '';
  const destinationId = propDestinationId || '';
  const folderId =
    propFolderId ||
    (activeFolderIds && activeFolderIds.length > 0 ? activeFolderIds[0] : '') ||
    '';
  const folderName = propFolderName || 'Folder';

  // Log modal parameters
  console.log('MoveFolderModal - Props received:', {
    propTripId,
    propDestinationId,
    propFolderId,
    propFolderName,
    deleteAfterMove
  });
  console.log('MoveFolderModal - Resolved values:', {
    tripId,
    destinationId,
    folderId,
    folderName,
    deleteAfterMove
  });

  const [showTripDropdown, setShowTripDropdown] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState<TripOption | null>(null);
  const [trips, setTrips] = useState<TripOption[]>([]);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const tripDropdownRef = useRef<HTMLDivElement>(null);

  const { moveFolder, copyFolder, loading } = useMoveFolder(() => {
    handleClose();
  });

  // Fetch all trips on mount
  useEffect(() => {
    const fetchTrips = async () => {
      setLoadingTrips(true);
      try {
        const data = await tripsService.getAllTrips();
        // Filter out deleted trips, current trip, and map to our format
        const tripOptions = data
          .filter((t) => !t.deleted && t.id !== tripId)
          .map((t) => ({
            id: t.id,
            title: t.title,
            destinationCount: t.destinationCount
          }));
        setTrips(tripOptions);
      } catch (error) {
        console.error('Failed to fetch trips:', error);
      } finally {
        setLoadingTrips(false);
      }
    };
    if (show) {
      fetchTrips();
    }
  }, [show, tripId]);

  // Close dropdown if clicked outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        tripDropdownRef.current &&
        !tripDropdownRef.current.contains(event.target as Node)
      ) {
        setShowTripDropdown(false);
      }
    }
    if (showTripDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTripDropdown]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedTrip) return;

    const operation = deleteAfterMove ? 'Moving' : 'Copying';
    console.log(`${operation} folder to trip:`, {
      sourceTripId: tripId,
      sourceDestinationId: destinationId,
      sourceFolderId: folderId,
      targetTripId: selectedTrip.id
    });

    const payload = {
      sourceTripId: tripId,
      sourceDestinationId: destinationId || undefined,
      sourceFolderId: folderId,
      targetTripId: selectedTrip.id
    };

    if (deleteAfterMove) {
      // Move folder (deletes from source)
      await moveFolder(payload);
    } else {
      // Copy folder (keeps original)
      await copyFolder(payload);
    }
  };

  const canSubmit = selectedTrip && !loading;

  return (
    <ModalCustom
      title={
        deleteAfterMove ? `Move ${folderName}` : `Copy ${folderName} Folder`
      }
      show={show}
      onHide={handleClose}
    >
      <Form
        className='move-folder-modal'
        onSubmit={handleSubmit}
      >
        {/* Trip Selection */}
        <div style={{ marginBottom: '24px' }}>
          <p
            className='fs-6 font-libre'
            style={{ marginBottom: '4px' }}
          >
            {deleteAfterMove ? `Move To:` : `Copy To:`}
          </p>
          <div
            style={{ position: 'relative' }}
            ref={tripDropdownRef}
          >
            <Button
              variant='outline-secondary'
              className='w-100 text-start d-flex justify-content-between align-items-center'
              style={{
                minHeight: '38px',
                fontFamily: 'Libre Baskerville, serif',
                fontSize: '1rem',
                border: '1px solid #ced4da',
                background: '#fff',
                color: '#777777'
              }}
              onClick={() => setShowTripDropdown((v) => !v)}
              type='button'
              disabled={loadingTrips}
            >
              {loadingTrips
                ? 'Loading trips...'
                : selectedTrip?.title || 'Select a trip'}
              <span style={{ float: 'right' }}>▼</span>
            </Button>
            {showTripDropdown && !loadingTrips && (
              <div
                style={{
                  position: 'absolute',
                  zIndex: 10,
                  width: '100%',
                  background: '#fff',
                  border: '1px solid #ced4da',
                  borderTop: 'none',
                  maxHeight: 300,
                  overflowY: 'auto',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                {trips.length === 0 ? (
                  <div
                    style={{
                      padding: '12px 16px',
                      color: '#999',
                      fontStyle: 'italic'
                    }}
                  >
                    No trips available
                  </div>
                ) : (
                  trips.map((tripOption) => (
                    <div
                      key={tripOption.id}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        backgroundColor:
                          selectedTrip?.id === tripOption.id
                            ? '#f8f9fa'
                            : 'transparent',
                        fontFamily: 'Libre Baskerville, serif'
                      }}
                      onClick={() => {
                        setSelectedTrip(tripOption);
                        setShowTripDropdown(false);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        if (selectedTrip?.id !== tripOption.id) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <div>{tripOption.title}</div>
                      <small style={{ color: '#999' }}>
                        {tripOption.destinationCount} destination
                        {tripOption.destinationCount !== 1 ? 's' : ''}
                      </small>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <Button
          disabled={!canSubmit}
          variant='primary'
          className='mx-auto'
          width={142}
          type='submit'
        >
          {loading
            ? deleteAfterMove
              ? 'Moving...'
              : 'Copying...'
            : deleteAfterMove
              ? `Move to ${selectedTrip?.title || 'Trip'}`
              : `Copy to ${selectedTrip?.title || 'Trip'}`}
        </Button>
      </Form>
    </ModalCustom>
  );
};

export default MoveFolderModal;
